import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  ClickAwayListener,
  CircularProgress,
  FormControl,
  Grow,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import DeleteOutlineOutlinedIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import ImageOutlinedIcon from '@material-ui/icons/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import PermMediaOutlinedIcon from '@material-ui/icons/PermMediaOutlined';
import RefreshOutlinedIcon from '@material-ui/icons/RefreshOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { ToolbarV2, ListTags } from 'components';
import Modal from 'components/Modal/Modal';
import { useDebounce } from 'helpers';
import { actions } from 'store/uploads';
import { getEnvVar } from 'helpers';

const {
  getUploads,
  getUsageTypeTags,
  createUpload,
  updateUpload,
  deleteUpload,
  updateUploadFilters,
  resetUploadFilters,
  clearUploadsError
} = actions;

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  heading: {
    marginBottom: theme.spacing(2)
  },
  galleryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  assetCard: {
    borderRadius: 10,
    position: 'relative',
    overflow: 'hidden',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: theme.shadows[8]
    }
  },
  assetPreview: {
    height: 190,
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4ecf7 100%)',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    cursor: 'pointer'
  },
  assetPreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  assetPreviewFallback: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#5f6f86',
    gap: theme.spacing(1)
  },
  fileIcon: {
    fontSize: 48
  },
  assetOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.58), rgba(0, 0, 0, 0.08))'
  },
  assetTopBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    minHeight: 48,
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.75, 1.5),
    background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0.12))'
  },
  assetName: {
    color: '#fff',
    fontSize: '0.875rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: '0.01071em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25)
  },
  overlayIconButton: {
    color: '#fff',
    padding: 6
  },
  emptyState: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center'
  },
  loadingState: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    padding: theme.spacing(2)
  },
  statusText: {
    marginTop: theme.spacing(1),
    color: theme.palette.error.main
  },
  modalBody: {
    display: 'grid',
    gap: theme.spacing(2),
    minWidth: 420,
    [theme.breakpoints.down('xs')]: {
      minWidth: 'auto'
    }
  },
  fileField: {
    display: 'grid',
    gap: theme.spacing(1)
  },
  createFormLayout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(260px, 320px)',
    gap: theme.spacing(3),
    alignItems: 'stretch',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr'
    }
  },
  formFieldsColumn: {
    display: 'grid',
    gap: theme.spacing(2),
    alignContent: 'start'
  },
  uploadDropZone: {
    minHeight: 320,
    borderRadius: 12,
    border: '2px dashed #b8bfd1',
    background: '#f8fafc',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease'
  },
  uploadDropZoneActive: {
    borderColor: theme.palette.primary.main,
    background: '#eef4ff',
    boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.12)'
  },
  uploadDropZonePreview: {
    width: '100%',
    height: 220,
    borderRadius: 10,
    overflow: 'hidden',
    background: '#fff',
    border: '1px solid #dde3ee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  uploadPreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  uploadDropZoneContent: {
    display: 'grid',
    gap: theme.spacing(1),
    justifyItems: 'center'
  },
  uploadDropZoneIcon: {
    fontSize: 40,
    color: '#5f6f86'
  },
  helperText: {
    color: '#5f6f86'
  },
  detailGrid: {
    display: 'grid',
    gridTemplateRows: '1fr auto auto auto auto auto',
    gap: theme.spacing(1.5),
    height: '100%'
  },
  detailPreview: {
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #e1e6ef',
    background: '#111',
    minHeight: 0,
    position: 'relative'
  },
  detailAssetPreview: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden'
  },
  detailAssetPreviewImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    display: 'block'
  },
  detailDialogContent: {
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2)
  },
  detailCdnRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0
  },
  detailCdnText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailCopyButton: {
    padding: 4
  },
  detailNavButton: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    color: '#fff',
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.65)'
    }
  },
  detailNavButtonDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed'
  },
  detailNavButtonLeft: {
    left: theme.spacing(1)
  },
  detailNavButtonRight: {
    right: theme.spacing(1)
  },
  filterButton: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selectedFilterButton: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  },
  sizeFilterPanel: {
    padding: theme.spacing(2),
    width: 300,
    display: 'grid',
    gap: theme.spacing(2)
  },
  sizeFilterInputs: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(1)
  },
  uploadDropZoneError: {
    borderColor: '#d32f2f',
  }
}));

const acceptTypes = getEnvVar('ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS') ? getEnvVar('ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS')?.split(',') : ['image/jpeg','image/png','image/jpg'];
const acceptTypesLabel = getEnvVar('ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS_LABEL') ? getEnvVar('ADM_SUPPORTED_UPLOAD_IMAGE_FORMATS_LABEL') : `JPEG, PNG and JPG`;
const acceptLimit = Number(getEnvVar('ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB') ? getEnvVar('ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB') : 10) * 1024 * 1024;
const acceptLimitLabel = getEnvVar('ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB') ? getEnvVar('ADM_MAX_FILE_SIZE_WITHOUT_USAGE_TYPE_TAG_IN_MB') : `10`;

const formatBytes = value => {
  const size = Number(value || 0);

  if (!size) {
    return 'Unknown size';
  }

  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const getAssetId = asset => asset?.id || asset?._id || asset?.contentId || asset?.blobId;

const getAssetName = asset => String(asset?.fileName || asset?.name || '').trim();

const getAssetUrl = asset => String(asset?.cdnPublicUrl || asset?.publicUrl || '').trim();

const getAssetType = asset =>
  String(
    asset?.contentMimeType ||
      asset?.contentType ||
      asset?.mimeType ||
      asset?.type ||
      'Unknown type'
  ).trim();

const getAssetUsageTypeTag = asset =>
  String(asset?.usageTypeTag || asset?.standardUsageTypeTag || '').trim();

const getAssetSize = asset => Number(asset?.fileSize || asset?.size || 0) || 0;

const getAssetCustomTags = asset => {
  if (Array.isArray(asset?.customTags)) {
    return asset.customTags.filter(Boolean);
  }

  if (Array.isArray(asset?.customTag)) {
    return asset.customTag.filter(Boolean);
  }

  if (typeof asset?.customTag === 'string' && asset.customTag) {
    return [asset.customTag];
  }

  return [];
};

const isImageAsset = asset => {
  const assetType = getAssetType(asset).toLowerCase();
  const assetUrl = getAssetUrl(asset).toLowerCase();
  return (
    assetType.startsWith('image/') ||
    ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'].some(extension =>
      assetUrl.includes(extension)
    )
  );
};

const normalizeCustomTags = value =>
  String(value || '')
    .split(',')
    .map(tag => tag.trim())
    .filter(Boolean);

const sanitizeAssetFileName = value => {
  const withoutExtension = String(value || '').replace(/\.[^/.]+$/, '');
  const sanitized = withoutExtension
    .replace(/[^A-Za-z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');

  return sanitized || 'asset';
};

const getAssetFileExtension = value => {
  const fileName = String(value || '').trim();
  const extensionMatch = fileName.match(/(\.[^./\\]+)$/);
  return extensionMatch ? extensionMatch[1] : '';
};

const isImageFile = file => {
  if (!file) {
    return false;
  }

  const fileType = String(file.type || '').toLowerCase();
  const fileName = String(file.name || '').toLowerCase();

  return (
    fileType.startsWith('image/') ||
    ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg'].some(extension =>
      fileName.endsWith(extension)
    )
  );
};

const Uploads = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    getting,
    saving,
    deleting,
    list,
    usageTypeTags,
    error,
    filters
  } = useSelector(store => store.uploads);

  const propertyId = useSelector(
    store => store.user?.selectedBusinessContext?.businessContextId || 'Default'
  );
  
  const [filterDraft, setFilterDraft] = useState({
    sizeGte: '',
    sizeLte: ''
  });
  const [editorState, setEditorState] = useState({
    isOpen: false,
    mode: 'create',
    asset: null,
    file: null,
    fileNameOverride: '',
    usageTypeTag: '',
    customTags: ''
  });
  const [detailAsset, setDetailAsset] = useState(null);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [pendingMutation, setPendingMutation] = useState(false);
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState('');
  const [isSizeFilterOpen, setIsSizeFilterOpen] = useState(false);
  const sizeFilterAnchorRef = React.useRef(null);
  const [isSortFilterOpen, setIsSortFilterOpen] = useState(false);
  const sortFilterAnchorRef = React.useRef(null);
  const sortFilterPanelRef = React.useRef(null);
  const [isSortByMenuOpen, setIsSortByMenuOpen] = useState(false);
  const [isSortOrderMenuOpen, setIsSortOrderMenuOpen] = useState(false);
  const theme = useTheme();
  const debouncedSizeGte = useDebounce(filterDraft.sizeGte, 500);
  const debouncedSizeLte = useDebounce(filterDraft.sizeLte, 500);
  const isUpdateMode = editorState.mode === 'update';
  const isCreateMode = editorState.mode === 'create';
  const fileInputRef = useRef(null);
  const existingAssetPreviewUrl =
    isUpdateMode && isImageAsset(editorState.asset) ? getAssetUrl(editorState.asset) : '';
  const updatePreviewUrl = filePreviewUrl || existingAssetPreviewUrl;
  const normalizedFileNameOverride = String(editorState.fileNameOverride || '').trim();
  const selectedFileExtension = getAssetFileExtension(editorState.file?.name);
  const finalUploadFileName = `${normalizedFileNameOverride}${selectedFileExtension}`;
  const isFileNameValid = /^[A-Za-z0-9_-]+$/.test(normalizedFileNameOverride);
  const hasFileNameError = isCreateMode && !!editorState.file && (!normalizedFileNameOverride || !isFileNameValid);
  const [hasUploadError, setHasUploadError] = useState(false);
  const canSubmitEditor =
    !saving && !hasUploadError && 
    ((isUpdateMode && Boolean(editorState.file)) || (isCreateMode && Boolean(editorState.file) && !hasFileNameError));

  useEffect(() => {
    if (!editorState.file || !isImageFile(editorState.file)) {
      setFilePreviewUrl('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(editorState.file);
    setFilePreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [editorState.file]);

  const detailAssetIndex = useMemo(() => {
    if (!detailAsset) {
      return -1;
    }

    const detailId = getAssetId(detailAsset);
    const detailUrl = getAssetUrl(detailAsset);
    const detailName = getAssetName(detailAsset);

    return list.findIndex(asset => {
      const assetId = getAssetId(asset);
      if (detailId && assetId) {
        return detailId === assetId;
      }

      const assetUrl = getAssetUrl(asset);
      if (detailUrl && assetUrl) {
        return detailUrl === assetUrl;
      }

      return detailName && detailName === getAssetName(asset);
    });
  }, [detailAsset, list]);

  const canNavigatePrevious = detailAssetIndex > 0;
  const canNavigateNext = detailAssetIndex >= 0 && detailAssetIndex < list.length - 1;

  const showPreviousDetailAsset = () => {
    if (!canNavigatePrevious) {
      return;
    }

    setDetailAsset(list[detailAssetIndex - 1]);
  };

  const showNextDetailAsset = () => {
    if (!canNavigateNext) {
      return;
    }

    setDetailAsset(list[detailAssetIndex + 1]);
  };

  useEffect(() => {
    dispatch(getUsageTypeTags());
    dispatch(getUploads());
  }, [dispatch]);

  useEffect(() => {
    setFilterDraft({
      sizeGte: filters?.sizeGte || '',
      sizeLte: filters?.sizeLte || ''
    });
  }, [filters]);

  useEffect(() => {
    const currentSizeGte = filters?.sizeGte || '';
    const currentSizeLte = filters?.sizeLte || '';

    // Ignore stale debounced values after programmatic resets/dismiss actions.
    if (debouncedSizeGte !== (filterDraft.sizeGte || '') || debouncedSizeLte !== (filterDraft.sizeLte || '')) {
      return;
    }

    if (debouncedSizeGte === currentSizeGte && debouncedSizeLte === currentSizeLte) {
      return;
    }

    dispatch(
      updateUploadFilters({
        sizeGte: debouncedSizeGte,
        sizeLte: debouncedSizeLte
      })
    );
    dispatch(getUploads());
  }, [
    debouncedSizeGte,
    debouncedSizeLte,
    filterDraft.sizeGte,
    filterDraft.sizeLte,
    filters?.sizeGte,
    filters?.sizeLte,
    dispatch
  ]);

  useEffect(() => {
    if (!pendingMutation || saving) {
      return;
    }

    if (!error) {
      setEditorState({
        isOpen: false,
        mode: 'create',
        asset: null,
        file: null,
        fileNameOverride: '',
        usageTypeTag: '',
        customTags: ''
      });
    }

    setPendingMutation(false);
  }, [pendingMutation, saving, error]);

  useEffect(() => {
    if (!detailAsset) {
      return undefined;
    }

    const onKeyDown = event => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        showPreviousDetailAsset();
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        showNextDetailAsset();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [detailAsset, showNextDetailAsset, showPreviousDetailAsset]);

  const searchOptions = {
    searchPlaceholder: 'Search uploads by name, or CDN URL',
    searchAction: payload => dispatch(updateUploadFilters(payload)),
    searchKey: 'searchInput',
    searchValue: filters?.searchInput || ''
  };

  const refreshUploads = () => {
    dispatch(getUploads());
  };

  const clearFilters = () => {
    setFilterDraft({
      sizeGte: '',
      sizeLte: ''
    });
    setIsSizeFilterOpen(false);
    dispatch(resetUploadFilters());
    dispatch(getUploads());
  };

  const isSizeFilterSelected = Boolean(filterDraft.sizeGte || filterDraft.sizeLte);

  const toggleSizeFilter = () => {
    setIsSizeFilterOpen(previousState => !previousState);
  };

  const closeSizeFilter = event => {
    if (sizeFilterAnchorRef.current && sizeFilterAnchorRef.current.contains(event.target)) {
      return;
    }
    setIsSizeFilterOpen(false);
  };

  const resetSizeFilter = () => {
    setFilterDraft({
      sizeGte: '',
      sizeLte: ''
    });
  };

  const toggleSortFilter = () => {
    setIsSortFilterOpen(previousState => !previousState);
  };

  const closeSortFilter = event => {
    // Don't close if a Select menu is open
    if (isSortByMenuOpen || isSortOrderMenuOpen) {
      return;
    }
    if (
      (sortFilterAnchorRef.current && sortFilterAnchorRef.current.contains(event.target)) ||
      (sortFilterPanelRef.current && sortFilterPanelRef.current.contains(event.target))
    ) {
      return;
    }
    setIsSortFilterOpen(false);
  };

  const resetSortFilter = () => {
    dispatch(
      updateUploadFilters({
        sortBy: '',
        sortOrder: ''
      })
    );
    dispatch(getUploads());
  };

  const handleSortByChange = event => {
    const sortBy = event.target.value;
    const nextFilters = !sortBy
      ? {
          sortBy: '',
          sortOrder: ''
        }
      : {
          sortBy,
          sortOrder: filters?.sortOrder || 'ASC'
        };

    dispatch(
      updateUploadFilters(nextFilters)
    );
    dispatch(getUploads());
  };

  const handleSortOrderChange = event => {
    const sortOrder = event.target.value;
    dispatch(
      updateUploadFilters({
        sortBy: filters?.sortBy || '',
        sortOrder: sortOrder
      })
    );
    dispatch(getUploads());
  };

  const isSortFilterSelected = Boolean(filters?.sortBy || filters?.sortOrder);

  const openCreateDialog = () => {
    dispatch(clearUploadsError());
    setEditorState({
      isOpen: true,
      mode: 'create',
      asset: null,
      file: null,
      fileNameOverride: '',
      usageTypeTag: '',
      customTags: getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm'
    });
  };

  const openUpdateDialog = asset => {
    dispatch(clearUploadsError());
    setEditorState({
      isOpen: true,
      mode: 'update',
      asset,
      file: null,
      fileNameOverride: '',
      usageTypeTag: getAssetUsageTypeTag(asset),
      customTags: getAssetCustomTags(asset).join(', ')
    });
  };

  const closeEditor = () => {
    setHasUploadError(false);
    setEditorState({
      isOpen: false,
      mode: 'create',
      asset: null,
      file: null,
      fileNameOverride: '',
      usageTypeTag: '',
      customTags: getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm'
    });
  };

  const submitEditor = () => {
    if (!canSubmitEditor) {
      return;
    }

    const payload = isUpdateMode
      ? {
          file: editorState.file
        }
      : {
          file: editorState.file,
          fileNameOverride: finalUploadFileName,
          usageTypeTag: editorState.usageTypeTag,
          customTags: [getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm',...normalizeCustomTags(editorState.customTags).filter(tag => tag.toLowerCase() !== 'adm')]
        };

    setPendingMutation(true);

    if (editorState.mode === 'update') {
      dispatch(updateUpload({ ...payload, asset: editorState.asset }));
      return;
    }

    dispatch(createUpload(payload));
  };

  const applySelectedFile = selectedFile => {
    setHasUploadError(false);
    dispatch(clearUploadsError());
    if (!selectedFile) {
      return;
    }

    const isValid = isValidFile(selectedFile);
    if (!isValid) {
      setHasUploadError(true);
      dispatch(clearUploadsError());
      setEditorState(current => ({
        ...current,
        file: null
      }));
      return;
    }
    setHasUploadError(false);
    setEditorState(current => ({
      ...current,
      file: selectedFile,
      fileNameOverride:
        current.mode === 'create'
          ? (selectedFile ? sanitizeAssetFileName(selectedFile.name) : '')
          : current.fileNameOverride
    }));
  };

  const openFilePicker = () => { 
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileInputChange = event => {
    const selectedFile = event.target.files?.[0] || null;
    applySelectedFile(selectedFile);
    event.target.value = '';
  };

  const isValidFile = (file) => {
    return acceptTypes.includes(file.type) && file.size <= acceptLimit;
  };

  const handleFileDrop = event => {
    event.preventDefault();
    event.stopPropagation();
    setIsFileDragActive(false);
    const droppedFile = event.dataTransfer?.files?.[0] || null;
    applySelectedFile(droppedFile);
  };

  const confirmDelete = async() => {
    const assetId = getAssetId(assetToDelete);

    if (!assetId) {
      return;
    }

    try {
      await dispatch(deleteUpload({ id: assetId }));
      setAssetToDelete(null);
      setDetailAsset(null);
    } catch (error) {
      console.error('Error deleting upload:', error);
    }
  };

  const [copiedUrl, setCopiedUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const copyUrl = async asset => {
    const assetUrl = getAssetUrl(asset);

    if (!assetUrl) {
      return;
    }

    await navigator.clipboard.writeText(assetUrl);
    setCopied(true);
    setCopiedUrl(assetUrl);
    setTimeout(() => {
      setCopied(false);
      setCopiedUrl('');
    }, 2000);
  };

  const uploadButtonGroup = useMemo(
    () => [
      {
        name: 'Upload',
        action: openCreateDialog
      },
      {
        name: 'Refresh',
        action: refreshUploads,
        startIcon: <RefreshOutlinedIcon />,
        disabled: getting
      }
    ],
    [getting]
  );

  const usageTypeOptions = useMemo(
    () =>
      (usageTypeTags || []).map(tag => ({
        name: 'usageTypeTag',
        value: tag,
        checked: filters?.usageTypeTag === tag
      })),
    [usageTypeTags, filters?.usageTypeTag]
  );

  const filterOptions = useMemo(
    () => ({
      //   {/* Commenting out Usage Type as part of the bug AB#1106770 */}
      // filters: [
      //   {
      //     options: usageTypeOptions,
      //     title: 'Usage Type',
      //     onChange: selectedValues => {
      //       dispatch(
      //         updateUploadFilters({
      //           usageTypeTag: selectedValues[selectedValues.length - 1] || ''
      //         })
      //       );
      //     }
      //   },

      // ],
      customFilters: [
        {
          component: (
            <>
              <Button
                aria-label="Filter Sort"
                ref={sortFilterAnchorRef}
                className={isSortFilterSelected ? classes.selectedFilterButton : classes.filterButton}
                onClick={toggleSortFilter}
                endIcon={<ExpandMoreIcon />}>
                Sort
              </Button>
              <Popper open={isSortFilterOpen} anchorEl={sortFilterAnchorRef.current} role={undefined} transition placement="bottom-start">
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                    }}>
                    <Paper ref={sortFilterPanelRef}>
                      <ClickAwayListener onClickAway={closeSortFilter}>
                        <Box className={classes.sizeFilterPanel}>
                          <Box
                            display="grid"
                            gridTemplateColumns="1fr 1fr"
                            style={{
                              columnGap: theme.spacing(2),
                              marginBottom: theme.spacing(2)
                            }}>
                            <FormControl variant="outlined" size="small" fullWidth>
                              <InputLabel
                                shrink={Boolean(filters?.sortBy)}
                                style={{
                                  transform: filters?.sortBy ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, 10px) scale(1)',
                                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                                }}>
                                Sort By
                              </InputLabel>
                              <Select
                                value={filters?.sortBy || ''}
                                onChange={handleSortByChange}
                                onOpen={() => setIsSortByMenuOpen(true)}
                                onClose={() => setIsSortByMenuOpen(false)}
                                label={filters?.sortBy ? 'Sort By' : ''}>                                 
                                <MenuItem value="fileName">File Name</MenuItem>
                                <MenuItem value="fileSize">File Size</MenuItem>
                                {/* Commenting out Usage Type as part of the bug AB#1106770 */}
                                {/* <MenuItem value="usageTypeTag">Usage Type</MenuItem> */}
                                <MenuItem value="updatedOn">Created Time</MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl variant="outlined" size="small" fullWidth>
                              <InputLabel
                                shrink={Boolean(filters?.sortOrder)}
                                style={{
                                  transform: filters?.sortOrder ? 'translate(14px, -9px) scale(0.75)' : 'translate(14px, 10px) scale(1)',
                                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
                                }}>
                                Sort Order
                              </InputLabel>
                              <Select
                                value={filters?.sortOrder || ''}
                                onChange={handleSortOrderChange}
                                onOpen={() => setIsSortOrderMenuOpen(true)}
                                onClose={() => setIsSortOrderMenuOpen(false)}
                                label={filters?.sortOrder ? 'Sort Order' : ''}>
                                <MenuItem value="ASC">Ascending</MenuItem>
                                <MenuItem value="DESC">Descending</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>
                          <Box display="flex" justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="text"
                              onClick={resetSortFilter}
                              disabled={!isSortFilterSelected}>
                              Reset Sort
                            </Button>
                          </Box>
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </>
          )
        },
        {
          component: (
            <>
              <Button
                aria-label="Filter Size"
                ref={sizeFilterAnchorRef}
                className={isSizeFilterSelected ? classes.selectedFilterButton : classes.filterButton}
                onClick={toggleSizeFilter}
                endIcon={<ExpandMoreIcon />}>
                Size
              </Button>
              <Popper open={isSizeFilterOpen} anchorEl={sizeFilterAnchorRef.current} role={undefined} transition placement="bottom-start">
                {({ TransitionProps, placement }) => (
                  <Grow
                    {...TransitionProps}
                    style={{
                      transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'
                    }}>
                    <Paper>
                      <ClickAwayListener onClickAway={closeSizeFilter}>
                        <Box className={classes.sizeFilterPanel}>
                          <Typography variant="body2" color="textSecondary">
                            Size (in KB)
                          </Typography>
                          <Box className={classes.sizeFilterInputs}>
                            <TextField
                              label="Min"
                              size="small"
                              type="number"
                              value={filterDraft.sizeGte}
                              variant="outlined"
                              inputProps={{ min: 0 }}

                              onKeyDown={event => {
                                if (event.key === '-') {
                                  event.preventDefault();
                                }
                              }}

                              onChange={event => {
                                const { value } = event.target;
                                setFilterDraft(current => ({
                                  ...current,
                                  sizeGte: value
                                }));
                              }}
                            />
                            <TextField
                              label="Max"
                              size="small"
                              type="number"
                              value={filterDraft.sizeLte}
                              variant="outlined"
                              inputProps={{ min: 0 }}

                              onKeyDown={event => {
                                if (event.key === '-') {
                                  event.preventDefault();
                                }
                              }}

                              onChange={event => {
                                const { value } = event.target;
                                setFilterDraft(current => ({
                                  ...current,
                                  sizeLte: value
                                }));
                              }}
                            />
                          </Box>
                          <Box display="flex" justifyContent="flex-end">
                            <Button
                              size="small"
                              variant="text"
                              onClick={resetSizeFilter}
                              disabled={!isSizeFilterSelected}>
                              Reset Size
                            </Button>
                          </Box>
                        </Box>
                      </ClickAwayListener>
                    </Paper>
                  </Grow>
                )}
              </Popper>
            </>
          )
        }
      ],
      resetFilters: clearFilters
    }),
    [
      usageTypeOptions,
      filters?.sortBy,
      filters?.sortOrder,
      isSortFilterOpen,
      isSortByMenuOpen,
      isSortOrderMenuOpen,
      isSortFilterSelected,
      toggleSortFilter,
      closeSortFilter,
      handleSortByChange,
      handleSortOrderChange,
      resetSortFilter,
      isSizeFilterOpen,
      isSizeFilterSelected,
      filterDraft.sizeGte,
      filterDraft.sizeLte,
      classes.filterButton,
      classes.selectedFilterButton,
      classes.sizeFilterPanel,
      classes.sizeFilterInputs,
      theme,
      dispatch
    ]
  );

  return (
    <div className={classes.root}>
      <ToolbarV2
        searchOptions={searchOptions}
        filterOptions={filterOptions}
        showFilterToggle={true}
        buttonsGroup={uploadButtonGroup}
        action={getUploads()}
      />

      {getting ? (
        <div className={classes.loadingState}>
          <CircularProgress />
        </div>
      ) : list.length === 0 ? (
        <Card className={classes.emptyState}>
          <Typography variant="h6">No images found</Typography>
          {/* Commenting out out as part of the ticket: AB#1118208 */}
          {/* <Typography variant="body2" color="textSecondary">
            Adjust the Azure tag and size filters, or upload a new asset to this property.
          </Typography> */}
        </Card>
      ) : (
        <div className={classes.galleryGrid}>
          {list.map(asset => {
            const assetName = getAssetName(asset) || 'Unnamed asset';
            const assetUrl = getAssetUrl(asset);
            const metadataTooltip = (
              <Box style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <Typography variant="body2" style={{ color: '#fff' }}>Type: {getAssetType(asset)}</Typography>
                <Typography variant="body2" style={{ color: '#fff' }}>Size: {formatBytes(getAssetSize(asset))}</Typography>
                {/* Commenting out Usage Type as part of the bug AB#1106770 */}
                {/* <Typography variant="body2" style={{ color: '#fff' }}>Usage Type: {getAssetUsageTypeTag(asset) || 'None'}</Typography> */}
                <Typography variant="body2" style={{ color: '#fff' }}>Custom Tags: {getAssetCustomTags(asset).join(', ') || 'None'}</Typography>
              </Box>
            );

            return (
              <Card key={getAssetId(asset) || assetUrl || assetName} className={classes.assetCard}>
                 <div
                   className={classes.assetPreview}
                   role="button"
                   tabIndex={0}
                   onClick={() => setDetailAsset(asset)}
                   onKeyDown={event => {
                     if (event.key === 'Enter' || event.key === ' ') {
                       event.preventDefault();
                       setDetailAsset(asset);
                     }
                   }}>
                   <Tooltip title={metadataTooltip} arrow>
                     <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                       {isImageAsset(asset) && assetUrl ? (
                         <img
                           alt={assetName}
                           className={classes.assetPreviewImage}
                           src={assetUrl}
                         />
                       ) : (
                         <div className={classes.assetPreviewFallback}>
                           <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
                           <Typography variant="body2">File asset</Typography>
                         </div>
                       )}
                     </div>
                   </Tooltip>
                   <Tooltip title={assetName} arrow>
                     <div className={classes.assetTopBar}>
                       <Typography className={classes.assetName} title={assetName}>
                         {assetName}
                       </Typography>
                     </div>
                   </Tooltip>
                 </div>
                <div className={classes.assetOverlay}>
                    <div className={classes.actionGroup}>
                      <Tooltip title="Preview">
                        <IconButton
                          className={classes.overlayIconButton}
                          onClick={event => {
                            event.stopPropagation();
                            setDetailAsset(asset);
                          }}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={copiedUrl === assetUrl ? 'Copied!' : 'Copy URL'}>
                        <span>
                          <IconButton
                            className={classes.overlayIconButton}
                            disabled={!assetUrl}
                            onClick={event => {
                              event.stopPropagation();
                              copyUrl(asset);
                            }}>
                            <FileCopyOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {/* <Tooltip title="update">
                        <IconButton
                          className={classes.overlayIconButton}
                          onClick={event => {
                            event.stopPropagation();
                            openUpdateDialog(asset);
                          }}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip> */}
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            className={classes.overlayIconButton}
                            disabled={deleting}
                            onClick={event => {
                              event.stopPropagation();
                              setAssetToDelete(asset);
                            }}>
                            <DeleteOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </div>
                  </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={editorState.isOpen}
        onClose={closeEditor}
        title={editorState.mode === 'update' ? 'Update Asset' : 'Upload Image'}
        closeButtonStyle={{ color: '#d32f2f' }}
        actions={
          <>
            <Button onClick={closeEditor} disabled={saving}>
              Cancel
            </Button>
            <Button color="primary" variant="contained" disabled={!canSubmitEditor} onClick={submitEditor}>
              {saving ? 'Saving...' : editorState.mode === 'update' ? 'Update' : 'Upload'}
            </Button>
          </>
        }>
        <div className={classes.modalBody}>
          <input
            ref={fileInputRef}
            hidden
            type="file"
            accept={acceptTypes}
            onChange={handleFileInputChange}
          />

          {isCreateMode && (
            <div className={classes.createFormLayout}>
              <div className={classes.formFieldsColumn}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="File Name"
                  value={editorState.fileNameOverride}
                  disabled={!editorState.file}
                  onChange={event => {
                    const { value } = event.target;
                    setEditorState(current => ({
                      ...current,
                      fileNameOverride: value
                    }));
                  }}
                  error={hasFileNameError}
                  helperText={hasFileNameError ? 'Use letters, numbers, underscore, or hyphen only.' : ''}
                />

                {/* Commenting out Usage Type as part of the bug AB#1106770 */}
                {/* <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel id="asset-editor-usage-type-label">Usage Type</InputLabel>
                  <Select
                    labelId="asset-editor-usage-type-label"
                    label="Usage Type"
                    value={editorState.usageTypeTag}
                    onChange={event => {
                      const { value } = event.target;
                      setEditorState(current => ({
                        ...current,
                        usageTypeTag: value
                      }));
                      if (editorState.file) {
                        setHasUploadError(!isValidFile(editorState.file, value));
                      }
                    }}>
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {(usageTypeTags || []).map(tag => (
                      <MenuItem key={tag} value={tag}>
                        {tag}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl> */}

                <ListTags
                protectedTags={[getEnvVar('ADM_UPLOAD_DEFAULT_TAG') || 'adm']}
                  tags={normalizeCustomTags(editorState.customTags)}
                  modifyTags={updatedTags => {
                    setEditorState(current => ({
                      ...current,
                      customTags: updatedTags.join(', ')
                    }));
                  }}
                  allTags={[]}
                  isTemplateReadOnly={false}
                  label="Custom Tags"
                />
              </div>

              <div
                className={`${classes.uploadDropZone} ${isFileDragActive ? classes.uploadDropZoneActive : ''} ${hasUploadError ? classes.uploadDropZoneError: ''}`}
                role="button"
                tabIndex={0}
                onClick={openFilePicker}
                onKeyDown={event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openFilePicker();
                  }
                }}
                onDragEnter={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsFileDragActive(true);
                }}
                onDragOver={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsFileDragActive(true);
                  if (event.dataTransfer) {
                    event.dataTransfer.dropEffect = 'copy';
                  }
                }}
                onDragLeave={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setIsFileDragActive(false);
                }}
                onDrop={handleFileDrop}>
                    <Typography variant="body2" className={classes.helperText}>
                     Note: Supports only {acceptTypesLabel} images upto {acceptLimitLabel} MB in size.
                    </Typography>
                {filePreviewUrl ? (
                  <div className={classes.uploadDropZonePreview}>
                    <img alt={editorState.file?.name || 'Upload preview'} className={classes.uploadPreviewImage} src={filePreviewUrl} />
                  </div>
                ) : (
                  <div className={classes.uploadDropZonePreview}>
                    <ImageOutlinedIcon className={classes.uploadDropZoneIcon} />
                  </div>
                )}
                <div className={classes.uploadDropZoneContent}>
                  <Typography variant="body1">Drag and drop an image here</Typography>
                  <Typography variant="body2" className={classes.helperText}>
                    or click to browse
                  </Typography>
                  {editorState.file && (
                    <Typography variant="body2" className={classes.helperText}>
                      {editorState.file.name}
                    </Typography>
                  )}
                </div>
              </div>
            </div>
          )}

          {isUpdateMode && (
            <div
              className={`${classes.uploadDropZone} ${isFileDragActive ? classes.uploadDropZoneActive : ''}`}
              role="button"
              tabIndex={0}
              onClick={openFilePicker}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openFilePicker();
                }
              }}
              onDragEnter={event => {
                event.preventDefault();
                event.stopPropagation();
                setIsFileDragActive(true);
              }}
              onDragOver={event => {
                event.preventDefault();
                event.stopPropagation();
                setIsFileDragActive(true);
                if (event.dataTransfer) {
                  event.dataTransfer.dropEffect = 'copy';
                }
              }}
              onDragLeave={event => {
                event.preventDefault();
                event.stopPropagation();
                setIsFileDragActive(false);
              }}
              onDrop={handleFileDrop}>
              {updatePreviewUrl ? (
                <div className={classes.uploadDropZonePreview}>
                  <img
                    alt={editorState.file?.name || getAssetName(editorState.asset) || 'Upload preview'}
                    className={classes.uploadPreviewImage}
                    src={updatePreviewUrl}
                  />
                </div>
              ) : (
                <div className={classes.uploadDropZonePreview}>
                  <ImageOutlinedIcon className={classes.uploadDropZoneIcon} />
                </div>
              )}
              <div className={classes.uploadDropZoneContent}>
                <Typography variant="body1">Drag and drop a replacement image here</Typography>
                <Typography variant="body2" className={classes.helperText}>
                  or click to browse
                </Typography>
                {editorState.file && (
                  <Typography variant="body2" className={classes.helperText}>
                    {editorState.file.name}
                  </Typography>
                )}
              </div>
            </div>
          )}

          {!hasUploadError && error && <Typography className={classes.statusText}>{error}</Typography>}
          {hasUploadError && <Typography className={classes.statusText}>Image upload failed: Invalid file format or size. Please check the file and try again.</Typography>}
        </div>
      </Modal>

      <Modal
        open={!!detailAsset}
        onClose={() => setDetailAsset(null)}
        title={
          detailAsset
            ? `${getAssetName(detailAsset)}${detailAssetIndex >= 0 ? ` (${detailAssetIndex + 1}/${list.length})` : ''}`
            : 'Asset Details'
        }
        actions={
          detailAsset ? (
            <>
              {/* <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  openUpdateDialog(detailAsset);
                  setDetailAsset(null);
                }}>
                Update
              </Button> */}
              <Button
                color="primary"
                variant="contained"
                onClick={() => {
                  setAssetToDelete(detailAsset);
                  
                }}>
                Delete
              </Button>
            </>
          ) : null
        }
        maxWidth="lg"
        paperStyle={{ height: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
        DialogContentProps={{ className: classes.detailDialogContent }}
        closeButtonStyle={{ color: '#d32f2f' }}>
        {detailAsset && (
          <div className={classes.detailGrid}>
            <div className={classes.detailPreview}>
              <Tooltip title="Previous">
                <IconButton
                  aria-label="Previous"
                  className={`${classes.detailNavButton} ${classes.detailNavButtonLeft} ${!canNavigatePrevious ? classes.detailNavButtonDisabled : ''}`}
                  onClick={() => {
                    if (!canNavigatePrevious) {
                      return;
                    }
                    showPreviousDetailAsset();
                  }}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
              <div className={classes.detailAssetPreview}>
                {isImageAsset(detailAsset) && getAssetUrl(detailAsset) ? (
                  <img
                    alt={getAssetName(detailAsset)}
                    className={classes.detailAssetPreviewImage}
                    src={getAssetUrl(detailAsset)}
                  />
                ) : (
                  <div className={classes.assetPreviewFallback}>
                    <InsertDriveFileOutlinedIcon className={classes.fileIcon} />
                    <Typography variant="body2">File asset</Typography>
                  </div>
                )}
              </div>
              <Tooltip title="Next">
                <IconButton
                  aria-label="Next"
                  className={`${classes.detailNavButton} ${classes.detailNavButtonRight} ${!canNavigateNext ? classes.detailNavButtonDisabled : ''}`}
                  onClick={() => {
                    if (!canNavigateNext) {
                      return;
                    }
                    showNextDetailAsset();
                  }}>
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
            </div>
            <Box className={classes.detailCdnRow}>
              {getAssetUrl(detailAsset) ? (
                <>
                  <Typography variant="body2">CDN URL:</Typography>
                  <Typography
                    variant="body2"
                    component="a"
                    href={getAssetUrl(detailAsset)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.detailCdnText}
                    title={getAssetUrl(detailAsset)}>
                    {getAssetUrl(detailAsset)}
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" className={classes.detailCdnText} title="Unavailable">
                  CDN URL: Unavailable
                </Typography>
              )}
              <Tooltip title={copied ? "Copied!" : "Copy CDN URL"}>
                <span>
                  <IconButton
                    className={classes.detailCopyButton}
                    disabled={!getAssetUrl(detailAsset)}
                    onClick={() => copyUrl(detailAsset)}>
                    <FileCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography variant="body2">Type: {getAssetType(detailAsset)}</Typography>
            {/* Commenting out Usage Type as part of the bug AB#1106770 */}
            {/* <Typography variant="body2">Usage Type: {getAssetUsageTypeTag(detailAsset) || 'None'}</Typography> */}
            <Typography variant="body2">Custom Tags: {getAssetCustomTags(detailAsset).join(', ') || 'None'}</Typography>
            <Typography variant="body2">Size: {formatBytes(getAssetSize(detailAsset))}</Typography>
          </div>
        )}
      </Modal>

      <Modal
        open={!!assetToDelete}
        onClose={() => setAssetToDelete(null)}
        title="Delete Asset"
        actions={
          <>
            <Button onClick={() => setAssetToDelete(null)}>Cancel</Button>
            <Button color="secondary" variant="contained" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }>
        <Typography variant="body2">
          Are you sure want to delete {assetToDelete ? getAssetName(assetToDelete) : 'this asset'} ? This action will also affect its associated template
        </Typography>
      </Modal>
    </div>
  );
};

export default Uploads;